import * as React from "react";
import { useFormBag } from "react-yup";
import { HelpText, HelpTextProps } from "./HelpText";
import { ValidationError } from "./ValidationError";

import classNames from "classnames";

interface FieldsetProps {
  legend: React.ReactNode;
  optional?: boolean;
  helpText?: HelpTextProps["helpText"];
  data: {
    name: string;
    id: number;
  }[];
  name: string;
  type: "radio" | "checkbox";
  disabled?: boolean;
}

export const Legend: React.FC<
  React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLLegendElement>,
    HTMLLegendElement
  >
> = ({ children, ...rest }) => {
  return (
    <legend
      className="mb-1"
      css={`
        font-size: 1rem;
      `}
      {...rest}
    >
      {children}
    </legend>
  );
};

export const Fieldset: React.FC<FieldsetProps> = ({
  legend,
  helpText,
  optional,
  name,
  data,
  type,
  disabled = false,
}) => {
  const { isTouched, getError, isChecked, field } = useFormBag();

  const touched = isTouched(name);
  const error = getError(name);

  return (
    <fieldset
      className="form-group"
      disabled={disabled}
      aria-required={!optional}
      aria-invalid={!!error && touched}
      aria-describedby={`${error && touched ? `${name}-error-message ` : ""}${
        helpText ? `${name}-help-text` : ""
      }`}
    >
      <Legend>
        {legend}{" "}
        {optional ? <small className="text-muted">(optional)</small> : "*"}
      </Legend>
      {helpText && <HelpText id={`${name}-help-text`} helpText={helpText} />}
      <div
        css={`
          @media (min-width: ${(p) => p.theme.smBreakpoint}) {
            column-count: 2;
          }
        `}
      >
        {data.map((n) => (
          <div key={n.id} className={`custom-control custom-${type} mb-2`}>
            <input
              type={type}
              className={classNames("custom-control-input", {
                "is-invalid": touched && error,
              })}
              id={`${name}__${n.id}`}
              name={name}
              value={n.id}
              checked={isChecked(name, n.id)}
              {...field}
            />
            <label
              className="custom-control-label"
              htmlFor={`${name}__${n.id}`}
            >
              {n.name}
            </label>
          </div>
        ))}
      </div>
      {error && touched && (
        <ValidationError errorId={`${name}-error-message`} message={error} />
      )}
    </fieldset>
  );
};

interface FieldsetFlexColumnsProps extends Omit<FieldsetProps, "data"> {
  data: {
    name: string;
    id: number;
  }[][];
}

export const FieldsetFlexColumns: React.FC<FieldsetFlexColumnsProps> = ({
  legend,
  helpText,
  optional,
  name,
  data,
  type,
  disabled = false,
}) => {
  const { isTouched, getError, isChecked, field } = useFormBag();

  const touched = isTouched(name);
  const error = getError(name);

  return (
    <fieldset
      className="form-group"
      disabled={disabled}
      aria-invalid={!!error && touched}
      aria-describedby={`${error && touched ? `${name}-error-message ` : ""}${
        helpText ? `${name}-help-text` : ""
      }`}
    >
      <Legend>
        {legend} {optional && <small className="text-muted">(optional)</small>}
      </Legend>
      {helpText && <HelpText id={`${name}-help-text`} helpText={helpText} />}
      <div className="d-sm-flex  mx-n3">
        {data.map((col, idx) => (
          <div
            key={idx}
            className="px-3"
            css={`
              flex: 0 0 50%;
            `}
          >
            {col.map((n) => (
              <div key={n.id} className={`custom-control custom-${type} mb-2`}>
                <input
                  type={type}
                  className={classNames(
                    "custom-control-input",
                    {
                      "is-invalid": touched && error,
                    },
                    n.id
                  )}
                  id={`${name}__${n.id}`}
                  name={name}
                  value={n.id}
                  checked={isChecked(name, n.id)}
                  {...field}
                />
                <label
                  className="custom-control-label"
                  htmlFor={`${name}__${n.id}`}
                >
                  {n.name}
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>
      {error && touched && (
        <ValidationError errorId={`${name}-error-message`} message={error} />
      )}
    </fieldset>
  );
};
